import { motion } from "framer-motion";
import { Heart, HandCoins, Users, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "@/constants/brand";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const handleGive = () => {
  if (BRAND.donateUrl) {
    window.open(BRAND.donateUrl, "_blank", "noopener");
  } else {
    toast.info("Donations open soon", {
      description:
        "Our secure giving page is launching shortly. Reach out below to support today.",
    });
  }
};

const WAYS = [
  {
    icon: HandCoins,
    title: "Give",
    text: "Your gift funds mentorship, wellness, and outreach for fathers in need.",
  },
  {
    icon: Users,
    title: "Volunteer",
    text: "Lend your time and skills as a mentor, organizer, or program leader.",
  },
  {
    icon: Heart,
    title: "Partner",
    text: "Bring DADication to your community, church, or organization.",
  },
];

export const GetInvolved = () => {
  return (
    <section
      id="get-involved"
      data-testid="get-involved-section"
      className="py-24 md:py-32 bg-[#e2562a] relative overflow-hidden"
    >
      <div className="absolute inset-0 grain-overlay opacity-[0.08] mix-blend-multiply" />
      <div className="absolute -bottom-32 -right-20 h-80 w-80 rounded-full bg-[#0b0d10]/25 blur-3xl" />
      <div className="absolute top-0 left-0 h-full w-px bg-white/10" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <span className="inline-flex items-center gap-3 text-[#0b0d10] uppercase tracking-[0.35em] text-xs font-bold mb-6">
            <span className="h-px w-10 bg-[#0b0d10]" />
            Get Involved
          </span>
          <h2 className="font-display uppercase text-5xl md:text-7xl text-white tracking-tight leading-[0.92]">
            Make an eternal impact
          </h2>
          <p className="mt-6 text-lg text-white/90 leading-relaxed max-w-xl">
            Every dollar and every hour helps a father heal, lead, and thrive.
            Join the movement and help build stronger families, one dad at a
            time.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="give-now-btn"
              onClick={handleGive}
              className="inline-flex items-center justify-center gap-2 bg-[#0b0d10] text-white hover:bg-black px-8 py-4 font-bold uppercase tracking-wider transition-all shadow-lg hover:-translate-y-0.5"
            >
              <Heart className="h-5 w-5" strokeWidth={2.5} />
              Give Now
            </button>
            <button
              data-testid="involved-contact-btn"
              onClick={() => scrollTo("contact")}
              className="inline-flex items-center justify-center gap-2 border border-[#0b0d10]/60 text-[#0b0d10] hover:bg-[#0b0d10] hover:text-white px-8 py-4 font-bold uppercase tracking-wider transition-all"
            >
              Volunteer or Partner
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-16">
          {WAYS.map((w, i) => (
            <motion.div
              key={w.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="bg-[#0b0d10] border border-[#0b0d10] p-8 hover:-translate-y-1 transition-transform"
            >
              <w.icon className="h-9 w-9 text-[#e2562a] mb-4" />
              <h3 className="font-display uppercase text-2xl text-white tracking-wide mb-2">
                {w.title}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed">{w.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
